import React, {useEffect, useState} from "react";
import Highcharts from "highcharts/highstock";
import HighchartsReact from "highcharts-react-official";
import {useTranslation} from "react-i18next";
import {ActvScopService} from "app/services/cp/ActvScopService";
import {toast} from "react-toastify";
import {EmissionService} from "app/services/cp/EmissionService";
import {HighchartsOptionsUtil} from "app/utils/highchartsOptionsUtil";
import {numberFormatter, parseNumberWithCommaDecimal} from "app/utils/stringUtil";
import {ChartUtil} from "app/utils/chartUtil";
import {ExistCpDataConstant} from "app/constants/cpConstant";
import DashboardLoading from "./DashboardLoading";
import DashboardNoData from "./DashboardNoData";
import {ResponseResultCode} from "app/model/response-api";
import {DashboardModuleProps} from "app/model/db/dashboardModuleProps";
import DashboardCard from "./DashboardCard";

const defaultChartBOption: any = {
  colors: ['#43C6DA', '#4B96F9', '#545CC1'],
  chart: {
    type: "column",
    height: 277,
    spacingTop: 0,
    spacingBottom: 0,
  },
  title: { text: '' },
  noData: HighchartsOptionsUtil.noData,
  exporting: { enabled: false },
  credits: { enabled: false },
  legend: {
    reversed: true,
    align: 'right',
    verticalAlign: 'top',
    layout: 'horizontal',
    symbolHeight: 10,
    padding: 10,
    itemStyle: {
      fontSize: '0.75rem',
      color: '#444444',
    },
  },
  plotOptions: {
    column: {
      stacking: 'normal',
      pointWidth: 20,
      borderWidth: 0,
    },
  },
  tooltip: {
    shared: true,
    formatter: function (this: Highcharts.TooltipFormatterContextObject) {
      let tooltip = `<span><strong style="font-size: 0.75rem;">${this.x}</strong></span><br/>`;
      this.points?.forEach((point: any) => {
        tooltip += `<span style="color:${point.color}">●&nbsp;</span>${point.series.name}: <strong style="font-size: 0.75rem;">${numberFormatter(point.y,2)}</strong> tCO₂eq<br/>`;
      });
      return tooltip;
    },
    style: {
      fontSize: '0.75rem',
    }
  },
  xAxis: {
    categories: Array.from({ length: 12 }, (_, i) => `${i + 1}월`), // 1월부터 12월까지
    min: 0,
    lineColor: '#999',
    lineWidth: 1,
    labels: {
      style: {
        color: '#444',
        fontSize: '0.7rem',
      },
    },
  },
  yAxis: {
    title: {
      text: "(tCO₂eq)",
      align: 'high',
      rotation: 0,
      offset: 0,
      y: -25,
      style: {
        color: '#444',
        fontSize: '0.65rem',
      },
    },
    min: 0,
    softMax: 10,
    lineColor: '#999',
    lineWidth: 1,
    gridLineColor: '#CCC',
    gridLineWidth: 1,
    gridLineDashStyle: 'ShortDash',
    labels: {
      x: -5,
      y: 4,
      style: {
        color: '#444',
        fontSize: "0.7rem",
      },
      formatter: function (this: any) {
        return parseNumberWithCommaDecimal(this.value);
      },
    },
  },
  series: ChartUtil.monthOfYearInZeroNumberArray,
  responsive: {
    rules: [
      {
        condition: {
          maxWidth: 500,
        },
        chartOptions: {
          chart: {
            spacingTop: 20,
          },
          xAxis: {
            labels: {
              style: {
                fontSize: "0.5rem",
              },
            },
          },
          yAxis: [
            {
              title: {
                y: -10,
                style: {
                  fontSize: "0.5rem",
                },
              },
              labels: {
                style: {
                  fontSize: "0.5rem",
                },
              },
            },
          ],
          legend: {
            align: 'center',
            verticalAlign: 'bottom',
            layout: 'horizontal',
            itemStyle: {
              fontSize: '0.6rem',
            },
          },
          tooltip: {
            style: {
              fontSize: '0.6rem',
            },
          },
          plotOptions: {
            column: {
              stacking: 'normal',
              pointWidth: undefined,
            },
          },
        },
      },
    ],
  },
};

const defaultScopeData = {
  scope1: {scopeNm: ''},
  scope2: {scopeNm: ''},
  scope3: {scopeNm: ''},
}

const defaultMmQnt = {
  mm1EmsnQnt: 0,
  mm2EmsnQnt: 0,
  mm3EmsnQnt: 0,
  mm4EmsnQnt: 0,
  mm5EmsnQnt: 0,
  mm6EmsnQnt: 0,
  mm7EmsnQnt: 0,
  mm8EmsnQnt: 0,
  mm9EmsnQnt: 0,
  mm10EmsnQnt: 0,
  mm11EmsnQnt: 0,
  mm12EmsnQnt: 0,
  total: 0,
}
const fcltDefaultMmQnt = (fcltNm: string) => ({
  ...defaultMmQnt,
  scopeNm: fcltNm
});

Highcharts.setOptions({
  ...HighchartsOptionsUtil.locale.kr.globalOptions,
  title: { text: '' },
  exporting: {enabled: false},
  credits: {enabled: false},
  tooltip: {shared: true, headerFormat: ''},
  accessibility: {enabled: false},
});

export default function ScopeEmissions(props: Readonly<DashboardModuleProps>) {
  const {t} = useTranslation();
  const toDayYear = new Date().getFullYear();
  const [scopUse, setScopUse] = useState<any>();
  const [optionB, setOptionB] = useState<any>(defaultChartBOption);
  const [resultTwo, setResultTwo] = useState<any>(defaultScopeData);
  const [existBoundarySetting, setExistBoundarySetting] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    const initialize = async () => {
      try {
        const response = await ActvScopService.getInstance().existCpData([ExistCpDataConstant.S]);
        if (response && response.resultCode === ResponseResultCode.Y) {
          if (!response.data?.SETTING) {
            setExistBoundarySetting(false);
            return;
          }
        }
        setExistBoundarySetting(true);
      } catch (e) {}
    };
    initialize();
  }, []);

  useEffect(() => {
    if(existBoundarySetting){
      setIsLoading(true);
      getScopeUseYn();
      getScopeData();
    } else {
      setIsLoading(false);
    }
  }, [existBoundarySetting]);

  function getScopeUseYn (){
    ActvScopService.getInstance().getCheckScope().then((response: any) => {
      if (response && response.resultCode === ResponseResultCode.Y && response.data) {
        setScopUse(response.data);
      }
    }).catch(() => {
      toast.error(t('common.message.error.system'));
    });
  }

  function getScopeData() {
    EmissionService.getInstance().getEmissiopnStatus(toDayYear.toString()).then((response) => {
      if (response && response.resultCode === ResponseResultCode.Y && response.data) {
        setResultTwo(makeChartB(response.data));
      }
    }).catch(() => {
      toast.error(t('common.message.error.system'));
    }).finally(() => {
      setIsLoading(false);
    });
  }

  function makeChartB(data: any) {
    const defaultScopes = {
      scope1: fcltDefaultMmQnt('Scope1'),
      scope2: fcltDefaultMmQnt('Scope2'),
      scope3: fcltDefaultMmQnt('Scope3'),
    };
    return convertChartData(data, defaultScopes);
  }

  function convertChartData(data: any, defaultScopes: any) {
    return data.reduce((acc: any, item: any) => {
      let {
        scopId,
        mm1EmsnQnt,
        mm2EmsnQnt,
        mm3EmsnQnt,
        mm4EmsnQnt,
        mm5EmsnQnt,
        mm6EmsnQnt,
        mm7EmsnQnt,
        mm8EmsnQnt,
        mm9EmsnQnt,
        mm10EmsnQnt,
        mm11EmsnQnt,
        mm12EmsnQnt,
      } = item;

      if (['연료사용', '폐기물소각', '공정배출'].includes(scopId)) {
        scopId = 'scope1';
      } else if (['전력 사용시설', '열/스팀 사용시설'].includes(scopId)) {
        scopId = 'scope2';
      } else {
        scopId = 'scope3';
      }

      for (let i = 1; i <= 12; i++) {
        const monthKey = `mm${i}EmsnQnt` as typeof item;
        acc[scopId][monthKey] = (acc[scopId][monthKey] || 0) + (item[monthKey] || 0);
      }

      acc[scopId].total = acc[scopId].total +
          mm1EmsnQnt +
          mm2EmsnQnt +
          mm3EmsnQnt +
          mm4EmsnQnt +
          mm5EmsnQnt +
          mm6EmsnQnt +
          mm7EmsnQnt +
          mm8EmsnQnt +
          mm9EmsnQnt +
          mm10EmsnQnt +
          mm11EmsnQnt +
          mm12EmsnQnt
      ;

      return acc;
    }, defaultScopes);
  }

  useEffect(() => {
    const series = [];
    if (scopUse && scopUse.scopN3UseYn === 'Y') {
      series.push({
        type: 'column',
        name: resultTwo.scope3.scopeNm,
        data: [
          resultTwo.scope3.mm1EmsnQnt,
          resultTwo.scope3.mm2EmsnQnt,
          resultTwo.scope3.mm3EmsnQnt,
          resultTwo.scope3.mm4EmsnQnt,
          resultTwo.scope3.mm5EmsnQnt,
          resultTwo.scope3.mm6EmsnQnt,
          resultTwo.scope3.mm7EmsnQnt,
          resultTwo.scope3.mm8EmsnQnt,
          resultTwo.scope3.mm9EmsnQnt,
          resultTwo.scope3.mm10EmsnQnt,
          resultTwo.scope3.mm11EmsnQnt,
          resultTwo.scope3.mm12EmsnQnt,
        ],
      });
    }
    if (scopUse && scopUse.scopN2UseYn === 'Y') {
      series.push({
        type: 'column',
        name: resultTwo.scope2.scopeNm,
        data: [
          resultTwo.scope2.mm1EmsnQnt,
          resultTwo.scope2.mm2EmsnQnt,
          resultTwo.scope2.mm3EmsnQnt,
          resultTwo.scope2.mm4EmsnQnt,
          resultTwo.scope2.mm5EmsnQnt,
          resultTwo.scope2.mm6EmsnQnt,
          resultTwo.scope2.mm7EmsnQnt,
          resultTwo.scope2.mm8EmsnQnt,
          resultTwo.scope2.mm9EmsnQnt,
          resultTwo.scope2.mm10EmsnQnt,
          resultTwo.scope2.mm11EmsnQnt,
          resultTwo.scope2.mm12EmsnQnt,
        ],
      });
    }
    if (scopUse && scopUse.scopN1UseYn === 'Y') {
      series.push({
        type: 'column',
        name: resultTwo.scope1.scopeNm,
        data: [
          resultTwo.scope1.mm1EmsnQnt,
          resultTwo.scope1.mm2EmsnQnt,
          resultTwo.scope1.mm3EmsnQnt,
          resultTwo.scope1.mm4EmsnQnt,
          resultTwo.scope1.mm5EmsnQnt,
          resultTwo.scope1.mm6EmsnQnt,
          resultTwo.scope1.mm7EmsnQnt,
          resultTwo.scope1.mm8EmsnQnt,
          resultTwo.scope1.mm9EmsnQnt,
          resultTwo.scope1.mm10EmsnQnt,
          resultTwo.scope1.mm11EmsnQnt,
          resultTwo.scope1.mm12EmsnQnt,
        ],
      });
    }
    setOptionB({
      ...optionB,
      series: series,
    });
  }, [resultTwo]);

  return (
    <DashboardCard
      title="온실가스 배출량 추이"
      isConfigMode={props.isConfigMode ?? false}
      moreButtonVisible={true}
      moreButtonUrl="/pc/gas/dischargstatus"
      dashboardConfig={props.dashboardConfig}
    >
      <DashboardLoading isLoading={isLoading} />
      {!isLoading && !existBoundarySetting ? (
        <DashboardNoData
          message="탄소플래너 운영경계를 설정하세요."
          link={{text: '경계 설정하기', to: '/cp/inventory'}}
        />
      ) : (
        <div className="inner-flex1">
          <div className="chart">
            <HighchartsReact highcharts={Highcharts} options={optionB}/>
          </div>
        </div>
      )}
    </DashboardCard>
  );
}
